import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { api, formatError, isValidEmail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";
import { useGoogleAuthEnabled } from "@/lib/authConfig";
import { consumeAfterLogin } from "@/lib/postLogin";
import Navbar from "@/components/Navbar";
import { LOGO, AUTH_PHOTOS } from "@/assets";

// Routhem to be swapped in via @font-face once delivered; Black Ops One is the
// bold tactical/stencil fallback until then.
const TAC = { fontFamily: "'Routhem', 'Black Ops One', 'Barlow Condensed', sans-serif" };

// The 2x2 split-grid photos — from the hashed asset registry.
const PHOTOS = AUTH_PHOTOS;

// The SINGLE entry screen: photo grid on the left, SIGN UP + LOG IN cards on the
// right. There is no separate /login page — /login redirects here — so the LOG IN
// card carries the real login form inline. Auth logic is a straight port of the old
// standalone page (unchanged): login -> resume any pending subscribe intent -> /feed.
export default function AccountEntry() {
  const googleEnabled = useGoogleAuthEnabled();
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidEmail(email)) { setError("Enter a valid email address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      applyAuth(data.token, data.user);
      if (consumeAfterLogin(navigate)) return;
      // Resume a subscribe-intent captured on /pricing -> straight into Stripe Checkout.
      const pending = localStorage.getItem("os_pending_plan");
      if (pending) {
        localStorage.removeItem("os_pending_plan");
        try {
          const { tier, interval } = JSON.parse(pending);
          const res = await api.post("/membership/checkout", { tier, interval, origin_url: window.location.origin });
          if (res.data?.checkout_url) { window.location.href = res.data.checkout_url; return; }
        } catch { navigate("/dashboard"); return; }
      }
      navigate("/feed");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1 w-full bg-[#0D0E0F] border border-[#3E3F42] focus:border-[#57682A] outline-none px-3 py-2.5 text-[#E8E8EA] placeholder:text-[#6B6D70] font-body transition-colors";
  const labelCls = "font-mono text-xs uppercase tracking-wide text-[#8B8D90]";

  return (
    <div className="min-h-screen bg-black" data-testid="account-entry-page">
      <Navbar />
      <div className="flex flex-col lg:flex-row" data-testid="account-entry">
      {/* LEFT — 2x2 photo grid with black padding/gaps (desktop) */}
      <div className="hidden lg:grid lg:w-1/2 lg:min-h-[calc(100vh-4rem)] grid-cols-2 grid-rows-2 gap-3 p-3 bg-black">
        {PHOTOS.map((src, i) => (
          <div key={i} className="overflow-hidden bg-[#0a0b09]">
            <img src={src} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>

      {/* MOBILE — compact 2x2 branded photo grid above the content */}
      <div className="grid lg:hidden grid-cols-2 gap-2 p-2 bg-black" data-testid="account-entry-mobile-grid">
        {PHOTOS.map((src, i) => (
          <div key={i} className="overflow-hidden bg-[#0a0b09] h-28 sm:h-40">
            <img src={src} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>

      {/* RIGHT — centered content block */}
      <div className="lg:w-1/2 lg:min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-8 bg-black">
        <div className="w-full max-w-md flex flex-col items-center text-center">
          {/* logo (transparent padding pre-trimmed) */}
          <img src={LOGO} alt="OpticSwap" className="w-32 sm:w-40 h-auto" data-testid="entry-logo" />

          {/* headline */}
          <h1 style={TAC} className="mt-4 text-4xl font-bold text-[#e6e6e6] leading-none">
            BUY | SELL | TRADE
          </h1>

          {/* eyebrow: MARKETPLACE flanked by olive lines */}
          <div className="mt-2 flex items-center gap-4">
            <span className="h-px w-10 sm:w-16 bg-[#849064]" />
            <span style={TAC} className="text-[22px] text-[#e6e6e6]">
              <span style={{ letterSpacing: "8px", paddingLeft: "8px" }}>MARKETPLACE</span>
            </span>
            <span className="h-px w-10 sm:w-16 bg-[#849064]" />
          </div>

          {/* Continue with Google — fast track (hidden when Google sign-in is disabled) */}
          {googleEnabled && (
            <div className="w-full mt-6">
              <GoogleAuthButton testId="entry-google-btn" />
              <div className="mt-3 flex items-center gap-3 text-xs font-mono text-muted-foreground uppercase tracking-widest">
                <div className="flex-1 h-px bg-sniper" /> or <div className="flex-1 h-px bg-sniper" />
              </div>
            </div>
          )}

          {/* SIGN UP card */}
          <div className="relative w-full mt-4 border border-[#6f7a56] bg-[#6f7a56]/10 p-5" data-testid="entry-signup-card">
            <span className="absolute -top-px -left-px w-5 h-5 border-t-2 border-l-2 border-[#6f7a56]" />
            <span className="absolute -bottom-px -right-px w-5 h-5 border-b-2 border-r-2 border-[#6f7a56]" />
            <p className="font-mono text-[10px] tracking-[0.25em] text-[#849064]">// NEW RECRUIT</p>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-foreground mt-1.5">Sign Up</h2>
            <p className="text-sm font-body text-secondary-foreground mt-1.5">Create your account to start buying, selling and trading your gear.</p>
            <Link
              to="/register"
              data-testid="entry-create-account"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#6f7a56] hover:bg-[#849064] text-[#e6e6e6] font-heading font-bold uppercase text-sm py-3 transition-colors"
            >
              Create Account <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* LOG IN card — inline functional form (this is the login; /login redirects here) */}
          <div className="w-full mt-4 border p-5 text-left" style={{ backgroundColor: "#111213", borderColor: "#2E2F31" }} data-testid="entry-login-card">
            <p className="font-mono text-[10px] tracking-[0.25em]" style={{ color: "#8B8D90" }}>// RETURNING CLIENT</p>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mt-1.5" style={{ color: "#E8E8EA" }}>Log In</h2>
            <form onSubmit={submit} className="mt-4 space-y-3" data-testid="entry-login-form">
              {error && (
                <div data-testid="login-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">
                  {error}
                  {!/account has been (closed|disabled|suspended)/i.test(error) && <Link to="/forgot-password" data-testid="login-error-forgot" className="block mt-1 underline text-olive-bright hover:text-volt">Forgot your password? Reset it here.</Link>}
                </div>
              )}
              <div>
                <label className={labelCls}>Email</label>
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className={inputCls}
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className={labelCls}>Password</label>
                  <Link to="/forgot-password" data-testid="login-forgot-link" className="font-mono text-[11px] uppercase text-olive-bright hover:text-volt transition-colors">Forgot?</Link>
                </div>
                <div className="relative mt-1">
                  <input
                    data-testid="login-password"
                    type={showPw ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="w-full bg-[#0D0E0F] border border-[#3E3F42] focus:border-[#57682A] outline-none px-3 py-2.5 pr-11 text-[#E8E8EA] placeholder:text-[#6B6D70] font-body transition-colors"
                  />
                  <button
                    type="button"
                    data-testid="login-password-toggle"
                    onClick={() => setShowPw((s) => !s)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    aria-pressed={showPw}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 transition-colors hover:opacity-80"
                    style={{ color: "#6B6D70" }}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button
                data-testid="login-submit"
                disabled={busy}
                className="w-full bg-[#57682A] hover:bg-[#6f7a56] disabled:opacity-50 text-[#E8E8EA] font-heading font-bold uppercase text-sm py-3 transition-colors"
              >
                {busy ? "Authenticating..." : "Log In"}
              </button>
            </form>
          </div>

          {/* footnote */}
          <p className="mt-4 font-mono text-[10px] tracking-[0.15em] uppercase" style={{ color: "#6B6D70" }} data-testid="entry-footnote">
            Selling professionally? Create an account and start listing today.
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}
